import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import FinDataGrid from "../components/findatagrid";

const PROVIDERS = ["llmstudio", "openai", "mistral", "deepseek"];
const WEIGHTS = [1, 2, 3, 4, 5];
const PROMPT_TYPES = ["SCREENER", "ANALYSIS", "TECHNICALS", "AI_NEWS", "TRAFFIC_LIGHT"];
const SECURITIES_OPTIONS = [
  "RANDOM_FOR_SCREENING_PHASE_1",
  "RANDOM_FOR_SCREENING_PHASE_2",
  "RANDOM_FOR_SCREENING_PHASE_3",
  "PORTFOLIO",
  "TECHNICALS_AFTER_PHASE3",
];
const AI_NEWS_JOB_NAMES = new Set([
  "AI News Worker",
  "AI News Enqueue Hourly",
  "AI News Enqueue Adhoc Hourly",
  "AI News Enqueue Adhoc Daily",
]);

const AdminTabAiModels = () => {
  const [models, setModels] = useState([]);
  const [cronjobs, setCronjobs] = useState([]);

  // Model dialog
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [modelDialogMode, setModelDialogMode] = useState("add"); // "add" | "edit"
  const [editModel, setEditModel] = useState({ model: "", weight: 3, llmProvider: "llmstudio" });

  // Job dialog
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);

  async function fetchModels() {
    try {
      const response = await fetch(process.env.REACT_APP_API_URI + "/api/ai_model_weights");
      const data = await response.json();
      setModels(data);
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchCronjobs() {
    try {
      const response = await fetch(process.env.REACT_APP_API_URI + "/api/cronjobs");
      const data = await response.json();
      // Show all jobs that have a parameters object
      const aiJobs = data.filter((row) => row.parameters && typeof row.parameters === "object");
      setCronjobs(aiJobs);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchModels();
    fetchCronjobs();
  }, []);

  // --- Model CRUD ---
  const handleAddModel = () => {
    setModelDialogMode("add");
    setEditModel({ model: "", weight: 3, llmProvider: "llmstudio" });
    setModelDialogOpen(true);
  };

  const handleEditModel = (row) => {
    setModelDialogMode("edit");
    setEditModel({ model: row.model, weight: row.weight, llmProvider: row.llmprovider || row.llmProvider });
    setModelDialogOpen(true);
  };

  const handleDeleteModel = async (model) => {
    if (!window.confirm(`Modell "${model}" wirklich löschen?`)) return;
    try {
      await fetch(process.env.REACT_APP_API_URI + "/api/ai_model_weights/" + encodeURIComponent(model), {
        method: "DELETE",
      });
      fetchModels();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveModel = async () => {
    const url = modelDialogMode === "add"
      ? process.env.REACT_APP_API_URI + "/api/ai_model_weights"
      : process.env.REACT_APP_API_URI + "/api/ai_model_weights/" + encodeURIComponent(editModel.model);
    const method = modelDialogMode === "add" ? "POST" : "PUT";

    try {
      await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: editModel.model,
          weight: editModel.weight,
          llmProvider: editModel.llmProvider,
        }),
      });
      setModelDialogOpen(false);
      fetchModels();
    } catch (error) {
      console.error(error);
    }
  };

  // --- Job Parameter Edit ---
  const isAiNewsJob = (name) => AI_NEWS_JOB_NAMES.has(name);

  const findProviderForModel = (modelName) => {
    if (!modelName) return "";
    const found = models.find((m) => m.model === modelName);
    return found ? (found.llmprovider || found.llmProvider || "") : "";
  };

  const getDefaultModel = () => (models.length > 0 ? (models[0].model || "") : "");

  const getDisplayParam = (row, key) => {
    const params = row.parameters || {};
    const direct = params?.[key];
    if (direct != null && String(direct).trim() !== "") return direct;

    if (isAiNewsJob(row.name)) {
      if (key === "type") return "AI_NEWS";
      if (key === "model") return getDefaultModel();
      if (key === "llmProvider") {
        const modelName = params?.model || getDefaultModel();
        return findProviderForModel(modelName);
      }
    }
    return "";
  };

  const handleEditJob = (row) => {
    const params = row.parameters || {};
    const aiNewsJob = isAiNewsJob(row.name);
    const defaultModel = getDefaultModel();
    const model = params.model || (aiNewsJob ? defaultModel : "");
    const llmProvider = params.llmProvider || (aiNewsJob ? findProviderForModel(model) : "");
    const type = params.type || (aiNewsJob ? "AI_NEWS" : "");

    setEditJob({
      id: row.id,
      name: row.name,
      originalParams: params,
      jobname: params.jobname || row.name,
      type,
      model,
      llmProvider,
      securities: params.securities || "",
    });
    setJobDialogOpen(true);
  };

  const handleSaveJob = async () => {
    if (!editJob) return;
    // Merge edited fields into existing parameters to preserve extra keys
    const parameters = {
      ...editJob.originalParams,
      jobname: editJob.jobname,
      type: editJob.type,
      model: editJob.model,
      llmProvider: editJob.llmProvider,
      securities: editJob.securities,
    };

    try {
      await fetch(process.env.REACT_APP_API_URI + "/api/cronjobs/" + editJob.id + "/parameters", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parameters),
      });
      setJobDialogOpen(false);
      fetchCronjobs();
    } catch (error) {
      console.error(error);
    }
  };

  // Auto-populate provider when model is selected
  const handleJobModelChange = (modelName) => {
    const found = models.find((m) => m.model === modelName);
    setEditJob({
      ...editJob,
      model: modelName,
      llmProvider: found ? (found.llmprovider || found.llmProvider) : editJob.llmProvider,
    });
  };

  // --- Column Definitions ---
  const modelColumns = [
    { field: "model", headerName: "Modell", width: 280, cellClassName: "name-column--cell" },
    { field: "weight", headerName: "Gewicht", width: 100 },
    {
      field: "llmprovider",
      headerName: "Provider",
      width: 140,
      valueGetter: (params) => params.row.llmprovider || params.row.llmProvider,
    },
    {
      field: "editBtn",
      headerName: "Edit",
      width: 80,
      renderCell: (params) => (
        <Button color="inherit" size="small" onClick={() => handleEditModel(params.row)}>
          Edit
        </Button>
      ),
    },
    {
      field: "deleteBtn",
      headerName: "Delete",
      width: 80,
      renderCell: (params) => (
        <Button color="error" size="small" onClick={() => handleDeleteModel(params.row.model)}>
          Del
        </Button>
      ),
    },
  ];

  const jobColumns = [
    { field: "name", headerName: "Job Name", width: 250, cellClassName: "name-column--cell" },
    {
      field: "paramType",
      headerName: "Prompt Typ",
      width: 140,
      valueGetter: (params) => getDisplayParam(params.row, "type"),
    },
    {
      field: "paramModel",
      headerName: "Modell",
      width: 260,
      valueGetter: (params) => getDisplayParam(params.row, "model"),
    },
    {
      field: "paramProvider",
      headerName: "Provider",
      width: 120,
      valueGetter: (params) => getDisplayParam(params.row, "llmProvider"),
    },
    {
      field: "paramSecurities",
      headerName: "Securities",
      width: 260,
      valueGetter: (params) => params.row.parameters?.securities || "",
    },
    {
      field: "editBtn",
      headerName: "Edit",
      width: 80,
      renderCell: (params) => (
        <Button color="inherit" size="small" onClick={() => handleEditJob(params.row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box mt={2}>
      {/* Bereich A: AI Modelle & Gewichtung */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5">AI Modelle & Gewichtung</Typography>
        <Button variant="outlined" color="secondary" onClick={handleAddModel}>
          Modell hinzufügen
        </Button>
      </Box>
      <FinDataGrid
        id="gridModels"
        height="35vh"
        density="compact"
        rows={models}
        columns={modelColumns}
        getRowId={(row) => row.model}
      />

      {/* Bereich B: AI Cron Job Parameter */}
      <Box mt={4} mb={1}>
        <Typography variant="h5">AI Cron Job Parameter</Typography>
      </Box>
      <FinDataGrid
        id="gridAiJobs"
        height="35vh"
        density="compact"
        rows={cronjobs}
        columns={jobColumns}
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        Änderungen an Cron-Job-Parametern werden nach Neustart des Services wirksam.
      </Alert>

      {/* Model Dialog */}
      <Dialog open={modelDialogOpen} onClose={() => setModelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{modelDialogMode === "add" ? "Modell hinzufügen" : "Modell bearbeiten"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Modell"
            fullWidth
            variant="standard"
            value={editModel.model}
            disabled={modelDialogMode === "edit"}
            onChange={(e) => setEditModel({ ...editModel, model: e.target.value })}
          />
          <FormControl fullWidth variant="standard" margin="dense">
            <InputLabel>Provider</InputLabel>
            <Select
              value={editModel.llmProvider}
              onChange={(e) => setEditModel({ ...editModel, llmProvider: e.target.value })}
            >
              {PROVIDERS.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="standard" margin="dense">
            <InputLabel>Gewicht</InputLabel>
            <Select
              value={editModel.weight}
              onChange={(e) => setEditModel({ ...editModel, weight: e.target.value })}
            >
              {WEIGHTS.map((w) => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setModelDialogOpen(false)}>Cancel</Button>
          <Button color="inherit" onClick={handleSaveModel}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Job Parameter Dialog */}
      <Dialog open={jobDialogOpen} onClose={() => setJobDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>AI Job Parameter bearbeiten</DialogTitle>
        <DialogContent>
          {editJob && (
            <>
              <TextField
                margin="dense"
                label="Job Name"
                fullWidth
                variant="standard"
                value={editJob.name}
                disabled
              />
              <FormControl fullWidth variant="standard" margin="dense">
                <InputLabel>Prompt Typ</InputLabel>
                <Select
                  value={editJob.type}
                  onChange={(e) => setEditJob({ ...editJob, type: e.target.value })}
                >
                  {PROMPT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="standard" margin="dense">
                <InputLabel>Modell</InputLabel>
                <Select
                  value={editJob.model}
                  onChange={(e) => handleJobModelChange(e.target.value)}
                >
                  {models.map((m) => (
                    <MenuItem key={m.model} value={m.model}>{m.model}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="standard" margin="dense">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={editJob.llmProvider}
                  onChange={(e) => setEditJob({ ...editJob, llmProvider: e.target.value })}
                >
                  {PROVIDERS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="standard" margin="dense">
                <InputLabel>Securities</InputLabel>
                <Select
                  value={editJob.securities}
                  onChange={(e) => setEditJob({ ...editJob, securities: e.target.value })}
                >
                  {SECURITIES_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setJobDialogOpen(false)}>Cancel</Button>
          <Button color="inherit" onClick={handleSaveJob}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTabAiModels;
